import type { CatalogParamModel } from '@/shared/generated'

import { startCase } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import {
	RHFSelect,
	RHFSwitch,
	RHFTextField,
} from '@/shared/components/HookForm'

import { AggregationTypeField } from './AggregationTypeField'
import { CompetitorListField } from './CompetitorListField'
import { PriceColumnField } from './PriceColumnField'

const humanize = (id: string) => startCase(id)

/** Renders one variant param, switched on its declared type. */
export const RuleParamField = ({
	param,
	name,
	context,
}: {
	param: CatalogParamModel
	name: string
	context?: {
		optimizationGroupId?: string
	}
}) => {
	const { t } = useTranslation()
	// `||` not `??`: an empty-string label must fall back to the humanized id —
	// intl.get('') throws "key is required".
	const label = param.label || humanize(param.id)

	const field = () => {
		switch (param.type) {
			case 'aggregation_type':
				return (
					<AggregationTypeField
						name={name}
						param={param}
						label={t(label, {
							defaultValue: label,
						})}
						testId={`pr-tactic-agg-${param.id}`}
					/>
				)
			case 'competitor_list':
				return (
					<CompetitorListField
						name={name}
						context={context}
						label={t(label, {
							defaultValue: label,
						})}
						testId={`pr-tactic-competitor-list-${param.id}`}
					/>
				)
			case 'price_column':
				return (
					<PriceColumnField
						name={name}
						label={t(label, {
							defaultValue: label,
						})}
						testId={`pr-tactic-price-column-${param.id}`}
					/>
				)
			case 'enum':
				return (
					<RHFSelect
						name={name}
						label={t(label, {
							defaultValue: label,
						})}
						options={(param.options ?? []).map((value) => ({
							value,
							label: value,
						}))}
						testId={`pr-tactic-field-${param.id}`}
					/>
				)
			case 'boolean':
				return (
					<RHFSwitch
						name={name}
						label={t(label, {
							defaultValue: label,
						})}
					/>
				)
			case 'integer':
			case 'decimal':
				return (
					<RHFTextField
						name={name}
						label={t(label, {
							defaultValue: label,
						})}
						type='number'
						data-testid={`pr-tactic-field-${param.id}`}
					/>
				)
			case 'string':
			default:
				return (
					<RHFTextField
						name={name}
						label={t(label, {
							defaultValue: label,
						})}
						data-testid={`pr-tactic-field-${param.id}`}
					/>
				)
		}
	}

	return <div data-testid={`pr-tactic-param-${param.id}`}>{field()}</div>
}
